import { useCallback, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { logger } from '../../utils/logger';
import { useAuth } from '../auth';
import fetchMigrationsFromService from '../../services/Migration/fetchMigrationsFromService';
import type { Migration } from '../../types';
import fetchOrganizationsFromService from '../../services/Organization/fetchOrganizationsFromService';
import { mapMigrationStatus } from '../../utils/migrationStatus';
import { computePollingInterval } from '../../utils/pollingInterval';

interface UseFetchJobsOptions {
  pollingInterval?: number; // milliseconds
  enablePolling?: boolean;
}

export const JOBS_QUERY_KEY = 'migration-jobs';

// Map a thrown fetch error to the display code the dashboard reacts to.
function mapJobsError(err: unknown): string | null {
  if (!err) return null;
  const msg = err instanceof Error ? err.message : String(err);
  const normalized = msg.toLowerCase();
  if (msg.includes('You are not authenticated to access migration logs')) {
    return 'You are not authenticated to access migration logs. Please contact your administrator for assistance.';
  }
  // Token expired: authenticatedFetch already redirects, so surface no banner.
  if (msg.includes('Authentication token expired')) {
    return null;
  }
  if (normalized.includes('403') || normalized.includes('forbidden')) {
    return 'forbidden';
  }
  return 'fetch_failed';
}

export const useFetchJobs = (options: UseFetchJobsOptions = {}) => {
  const { pollingInterval = 30000, enablePolling = true } = options;
  const { userInfo, accessToken } = useAuth();
  const queryClient = useQueryClient();
  const companyId = userInfo?.orgId || '';

  const query = useQuery<Migration[]>({
    queryKey: [JOBS_QUERY_KEY, companyId],
    enabled: Boolean(companyId && accessToken),
    // Adaptive cadence: poll tighter while any job is in flight so long-running jobs and
    // their progress bars stay live, and back off to the configured idle interval once
    // everything is terminal.
    refetchInterval: enablePolling
      ? (q) => computePollingInterval(q.state.data ?? [], { idleIntervalMs: pollingInterval })
      : false,
    queryFn: async (): Promise<Migration[]> => {
      logger.info('Fetching migration jobs ...');
      const started = performance.now();

      if (!companyId || !accessToken) {
        throw new Error('Missing customer ID or access token');
      }

      // Fetch all organizations the customer has access to
      const organizations = await fetchOrganizationsFromService(accessToken, companyId);
      const organizationIds = organizations.map((org) => org.id);

      logger.info(`Fetching migrations for ${organizationIds.length} organizations`);

      // Fetch migrations for all organizations
      const migrations = await fetchMigrationsFromService(accessToken, organizationIds);

      // Map backend status values (possibly numeric enums) to UI-friendly strings
      const migrationsWithMappedStatus = migrations.map((migration) => ({
        ...migration,
        status: mapMigrationStatus(migration.status)
      }));

      const allJobs: Migration[] = [...migrationsWithMappedStatus];

      logger.info(
        `Successfully fetched ${allJobs.length} migrations in ${(performance.now() - started).toFixed(2)} ms`
      );
      return allJobs;
    }
  });

  // Log fetch failures with the same categorization the dashboard reacts to.
  const queryError = query.error;
  useEffect(() => {
    if (!queryError) return;
    const msg = queryError instanceof Error ? queryError.message : String(queryError);
    if (msg.includes('Authentication token expired')) {
      logger.warn('Token expired during job fetch - redirect in progress');
    } else {
      logger.error('Failed to fetch migrations', { error: msg });
    }
  }, [queryError]);

  // Refetch on demand (after mutations, retry, etc.).
  const fetchJobs = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
  }, [queryClient]);

  // Kept for API compatibility; writes optimistically into the query cache.
  const setJobs: Dispatch<SetStateAction<Migration[]>> = useCallback(
    (update) => {
      queryClient.setQueryData<Migration[]>([JOBS_QUERY_KEY, companyId], (prev) => {
        const current = prev ?? [];
        return typeof update === 'function'
          ? (update as (p: Migration[]) => Migration[])(current)
          : update;
      });
    },
    [queryClient, companyId]
  );

  return {
    jobs: query.data ?? [],
    setJobs,
    loading: query.isLoading,
    error: mapJobsError(query.error),
    fetchJobs
  };
};

export default useFetchJobs;
